// Эталонные режимы — одно место, где задают, как прессовать.
//
// Раньше этих мест было два, и найти их не мог даже тот, кто их писал:
// режим позиции лежал в карточке ЛДСП («Справочники → ЛДСП → открыть строку»),
// режим матрицы — в «Текстурах пресса», а плитка «Характеристики прессов»,
// обещавшая «эталонный режим: температура, время, давление», не содержала ни
// одного из этих полей вовсе.
//
// Теперь один список: позиция, матрица, три цифры, видно где пусто.
//
// Порядок списка — от самых ходовых. Позиций в справочнике четыре сотни,
// прессуют из них два десятка, и заполнять надо именно их. Остальные лежат с
// прошлого года и ничего не стоят.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { productionAPI } from '../api';
import toast from 'react-hot-toast';
import { confirmToast } from '../components/ConfirmToast';
import { makeMatcher } from '../utils/search';

// Каждая величина — ДВА числа, и набираются они двумя полями.
//
// Раньше это была одна строка: «190 / 195». Косую черту теряли — в
// справочнике нашлась запись «190195», то есть сто девяносто тысяч градусов,
// и одна такая строка перекашивала весь отчёт по режиму. Ещё в неё писали
// буквы и пробелы, а что означает первое число, приходилось помнить.
//
// Два поля с подписями отвечают на это разом: перепутать нечего, потерять
// разделитель нечем, а кроме цифр в них ничего не набирается.
const FIELDS = [
  // У температуры это РАЗНЫЕ ПЛИТЫ, каждая со своим нагревателем: «190/195»
  // значит низ 190, верх 195, а не коридор от и до.
  { key: 'press_temperature',  title: 'Температура', unit: '°C',
    left: 'низ', right: 'верх', ph: ['190', '195'] },
  // А у выдержки и давления — именно коридор: «15/26» это от пятнадцати до
  // двадцати шести секунд.
  { key: 'press_hold_seconds', title: 'Время выдержки', unit: 'сек',
    left: 'от', right: 'до', ph: ['15', '26'] },
  { key: 'press_pressure',     title: 'Давление', unit: 'бар',
    left: 'от', right: 'до', ph: ['10', '12'] },
];

// Только цифры. Запятая — та же точка, всё остальное не набирается вовсе:
// поле не ругается на букву, а просто её не принимает.
function onlyNumber(v) {
  const t = String(v == null ? '' : v).replace(',', '.').replace(/[^0-9.]/g, '');
  const i = t.indexOf('.');
  return i < 0 ? t : t.slice(0, i + 1) + t.slice(i + 1).replace(/\./g, '');
}

// «190 / 195» → ['190','195'];  «12» → ['12',''];  мусор → ['',''].
function splitPair(raw) {
  const v = String(raw || '').match(/\d+(?:[.,]\d+)?/g) || [];
  return [(v[0] || '').replace(',', '.'), (v[1] || '').replace(',', '.')];
}

// Обратно в справочник. Одно число — так и пишем: и отчёт, и пресс читают
// его как «одинаково для обеих плит», и это верно.
function joinPair(pair) {
  const a = onlyNumber(pair[0]), b = onlyNumber(pair[1]);
  if (a && b) return a + ' / ' + b;
  return a || b || '';
}

const INP = {
  width: '100%', padding: '6px 8px', background: 'var(--bg)',
  border: '1px solid var(--border)', borderRadius: 7, color: 'var(--text)',
  fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit',
};
const TH = {
  textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 700,
  color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .3,
  borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
};
const TD = { padding: '6px 10px', borderBottom: '1px solid var(--border)',
             fontSize: 13, verticalAlign: 'middle' };

const BTN = (bg, extra = {}) => ({
  background: bg, border: '1px solid ' + bg, borderRadius: 8, color: '#fff',
  padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  fontFamily: 'inherit', ...extra,
});

// Одна ячейка: два числа с подписями, а под ними — что подставится, если
// оставить пусто.
//
// Пустая клетка почти никогда не значит «неизвестно». Значит «как у матрицы»,
// и это верно: матрица на то и общая. Показываем это серым прямо в клетке,
// чтобы технолог не заполнял тысячу строк тем, что и так уже работает.
//
// Правка хранится парой, а не склеенной строкой. Иначе набор ломается на
// полпути: стерев «низ», человек оставляет одно число, склейка отдаёт его
// без разделителя — и при следующей отрисовке цифра из «верха» прыгает в
// «низ» прямо под пальцем.
function Cell({ stored, field, pair, matrixRaw, hintRaw, onChange, onTake }) {
  const cur   = pair || splitPair(stored || '');
  const dirty = !!pair && joinPair(pair) !== (stored || '');
  const mPair = splitPair(matrixRaw || '');

  const setSide = (i, v) => {
    const next = [cur[0], cur[1]];
    next[i] = onlyNumber(v);
    onChange(next);
  };

  const under = joinPair(cur) ? ''
    : (matrixRaw ? `матрица: ${matrixRaw}`
      : (hintRaw ? `похожие: ${hintRaw}` : ''));

  return (
    <div>
      <div style={{ display: 'flex', gap: 6 }}>
        {[0, 1].map(i => (
          <div key={i} style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, color: 'var(--dim)', marginBottom: 2,
                          textAlign: 'center' }}>
              {i === 0 ? field.left : field.right}
            </div>
            <input
              value={cur[i]}
              onChange={e => setSide(i, e.target.value)}
              inputMode="decimal"
              placeholder={mPair[i] || field.ph[i]}
              style={{ ...INP, textAlign: 'center',
                borderColor: dirty ? 'var(--accent)' : 'var(--border)',
                background: dirty ? 'var(--accent-weak)' : 'var(--bg)' }} />
          </div>
        ))}
      </div>
      {under && (
        <div style={{ marginTop: 3, fontSize: 11, color: 'var(--dim)',
                      display: 'flex', gap: 6, alignItems: 'center' }}>
          <span>{under}</span>
          {/* Взять подсказку одним нажатием: сосед на той же матрице и той
              же толщине греется так же, и перенабирать это руками незачем */}
          {hintRaw && !matrixRaw && (
            <button onClick={() => onTake(splitPair(hintRaw))} style={{
              background: 'none', border: 'none', color: 'var(--accent)',
              cursor: 'pointer', fontSize: 11, padding: 0, fontWeight: 700,
              fontFamily: 'inherit' }}>взять</button>
          )}
        </div>
      )}
    </div>
  );
}

export default function EtalonModes() {
  const [data, setData]       = useState(null);
  const [edits, setEdits]     = useState({});   // 'p12.press_pressure' -> '25'
  const [saving, setSaving]   = useState(false);
  const [q, setQ]             = useState('');
  const [only, setOnly]       = useState('used');  // used | empty | all
  const [tab, setTab]         = useState('products');

  const load = useCallback(async () => {
    try {
      const r = await productionAPI.getEtalon();
      setData(r.data);
      setEdits({});
    } catch { toast.error('Не удалось загрузить'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (kind, id, key, value) =>
    setEdits(e => ({ ...e, [`${kind}${id}.${key}`]: value }));
  const get = (kind, id, key) => edits[`${kind}${id}.${key}`];

  const dirtyCount = useMemo(() => {
    if (!data) return 0;
    const seen = new Set();
    Object.keys(edits).forEach(k => seen.add(k.split('.')[0]));
    return seen.size;
  }, [edits, data]);

  const save = async () => {
    if (!dirtyCount) return;
    const products = {}, textures = {};
    Object.entries(edits).forEach(([k, v]) => {
      const [head, key] = k.split('.');
      const id = Number(head.slice(1));
      const bag = head[0] === 'p' ? products : textures;
      bag[id] = bag[id] || { id };
      bag[id][key] = joinPair(v);
    });
    setSaving(true);
    try {
      const r = await productionAPI.saveEtalon({
        products: Object.values(products), textures: Object.values(textures) });
      toast.success(`Сохранено: ${r.data.saved}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка сохранения');
    } finally { setSaving(false); }
  };

  // Проставить эталон там, где его нет: смены старше самой печати остались
  // без него, и сравнивать их отчёту не с чем вовсе.
  //
  // Только пустым. Кнопка «переписать всем» здесь была и убрана: раз отпечаток
  // можно стереть, он перестаёт быть доказательством, а весь смысл его именно
  // в этом. Смену меряют тем заданием, по которому она работала.
  const stampMissing = async () => {
    const ok = await confirmToast(
      'Проставить эталон сменам, у которых его нет?\n\n'
      + 'Это смены, отработанные до того, как программа начала отпечатывать '
      + 'режим. Уже отпечатанные не тронем — их эталон не меняется никогда.',
      { confirmText: 'Проставить' });
    if (!ok) return;
    try {
      const r = await productionAPI.stampMissingEtalon({});
      toast.success(`Отпечатано смен: ${r.data.stamped} из ${r.data.looked}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
    }
  };

  if (!data) return <div style={{ color: 'var(--muted)', padding: 20 }}>Загрузка…</div>;

  const match = makeMatcher(q);
  const rows = (data.rows || []).filter(r => {
    if (!match(r.name, r.full_name, r.matrix_name)) return false;
    if (only === 'used')  return r.jobs > 0;
    if (only === 'empty') return r.filled < 3;
    return true;
  });

  const s = data.summary || {};

  return (
    <div>
      {/* Сколько всего готово — чтобы работа была видна и было понятно, когда
          она кончилась */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        {[
          { t: 'Заполнено полностью', v: s.ready,   c: '#22c55e' },
          { t: 'Частично',            v: s.partial, c: '#f59e0b' },
          { t: 'Пусто',               v: s.empty,   c: 'var(--dim)' },
          { t: 'Не готовы из ходовых', v: s.empty_used, c: '#ef4444' },
        ].map(x => (
          <div key={x.t} style={{ background: 'var(--card)', borderRadius: 10,
                                  padding: '10px 16px', minWidth: 130 }}>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>{x.t}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: x.c }}>{x.v ?? 0}</div>
          </div>
        ))}
      </div>

      <div style={{ background: 'var(--accent-weak)', border: '1px solid var(--border)',
                    borderRadius: 10, padding: '10px 14px', marginBottom: 14,
                    fontSize: 12, color: 'var(--muted)', lineHeight: 1.6 }}>
        Пустая клетка — не «неизвестно», а «как у матрицы»: под ней написано, что
        подставится. Заполнять позицию нужно только тогда, когда она греется
        <b> не так</b>, как остальные на этой матрице.
        <br />
        Температура — две <b>разные плиты</b>: низ и верх греются порознь. Время
        и давление — <b>от и до</b>. Одно число вместо двух значит «одинаково»,
        и при следующем открытии оно встанет в левое поле.
        <br />
        Режим отпечатывается на смене при старте и остаётся при ней навсегда:
        сегодняшняя правка старые смены не тронет.
      </div>

      {/* Переключатель «позиции / матрицы»: матриц десяток, и правят их редко,
          но именно они закрывают собой сотни пустых позиций */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {[['products', `Позиции (${(data.rows || []).length})`],
          ['textures', `Матрицы (${(data.textures || []).length})`]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            background: tab === k ? 'var(--accent)' : 'var(--card)',
            border: '1px solid ' + (tab === k ? 'var(--accent)' : 'var(--border)'),
            borderRadius: 8, color: tab === k ? '#fff' : 'var(--muted)',
            padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            fontFamily: 'inherit' }}>{label}</button>
        ))}
      </div>

      {tab === 'products' && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap',
                      alignItems: 'center' }}>
          <input value={q} onChange={e => setQ(e.target.value)}
            placeholder="Поиск по названию…"
            style={{ ...INP, width: 260, padding: '8px 12px' }} />
          {[['used', 'Ходовые'], ['empty', 'Незаполненные'], ['all', 'Все']].map(([k, label]) => (
            <button key={k} onClick={() => setOnly(k)} style={{
              background: only === k ? 'var(--card)' : 'transparent',
              border: '1px solid ' + (only === k ? 'var(--accent)' : 'var(--border)'),
              borderRadius: 8, color: only === k ? 'var(--text)' : 'var(--muted)',
              padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit' }}>{label}</button>
          ))}
          <span style={{ color: 'var(--dim)', fontSize: 12 }}>
            показано {rows.length}
          </span>
        </div>
      )}

      <div style={{ background: 'var(--card)', borderRadius: 12, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 780 }}>
          <thead>
            <tr>
              <th style={TH}>{tab === 'products' ? 'Позиция' : 'Матрица'}</th>
              {tab === 'products' && <th style={TH}>Матрица</th>}
              {tab === 'products' && <th style={{ ...TH, textAlign: 'right' }}>Смен</th>}
              {FIELDS.map(f => (
                <th key={f.key} style={{ ...TH, width: 170 }}>
                  {f.title} <span style={{ color: 'var(--dim)', fontWeight: 400 }}>
                    {f.unit}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tab === 'products' && rows.map(r => (
              <tr key={r.id}>
                <td style={{ ...TD, maxWidth: 320 }}>
                  <div style={{ fontWeight: 600 }}>{r.name}</div>
                  {r.thickness && (
                    <div style={{ fontSize: 11, color: 'var(--dim)' }}>{r.thickness} мм</div>
                  )}
                </td>
                <td style={{ ...TD, color: 'var(--muted)', fontSize: 12 }}>
                  {r.matrix_name || '—'}
                  {/* Матрица почти у всех берётся из работы, а не из карточки:
                      поле в справочнике никто не заполнял, а цех всё это время
                      прессовал — и именно эта матрица подставит свой режим */}
                  {r.matrix_from === 'работа' && r.matrix_name && (
                    <div style={{ fontSize: 10, color: 'var(--dim)' }}>по сменам</div>
                  )}
                </td>
                <td style={{ ...TD, textAlign: 'right', color: r.jobs ? 'var(--text)' : 'var(--dim)' }}>
                  {r.jobs || '—'}
                </td>
                {FIELDS.map(f => (
                  <td key={f.key} style={TD}>
                    <Cell field={f} stored={r[f.key]} pair={get('p', r.id, f.key)}
                      matrixRaw={r[f.key + '_matrix']} hintRaw={r[f.key + '_hint']}
                      onChange={v => set('p', r.id, f.key, v)}
                      onTake={v => set('p', r.id, f.key, v)} />
                  </td>
                ))}
              </tr>
            ))}

            {tab === 'textures' && (data.textures || []).map(x => (
              <tr key={x.id}>
                <td style={TD}>
                  <div style={{ fontWeight: 600 }}>{x.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--dim)' }}>
                    {x.code} · позиций: {x.products}
                  </div>
                </td>
                {/* Та же ячейка, что и у позиций: матрицу правят реже, но
                    ошибиться в ней дороже — её режим уходит на сотню позиций */}
                {FIELDS.map(f => (
                  <td key={f.key} style={TD}>
                    <Cell field={f} stored={x[f.key]} pair={get('t', x.id, f.key)}
                      onChange={v => set('t', x.id, f.key, v)}
                      onTake={v => set('t', x.id, f.key, v)} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {tab === 'products' && rows.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--dim)' }}>
            Ничего не нашлось
          </div>
        )}
      </div>

      {/* Кнопка сохранения не уезжает вниз списка: строк четыреста, и
          докручивать до неё после каждой правки никто не станет */}
      {dirtyCount > 0 && (
        <div style={{ position: 'sticky', bottom: 0, marginTop: 12,
                      background: 'var(--card)', border: '1px solid var(--accent)',
                      borderRadius: 12, padding: '12px 16px', display: 'flex',
                      alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--text)', fontWeight: 600, fontSize: 14 }}>
            Изменено строк: {dirtyCount}
          </span>
          <button onClick={save} disabled={saving} style={BTN('var(--accent)')}>
            {saving ? 'Сохраняю…' : 'Сохранить'}
          </button>
          <button onClick={() => setEdits({})} style={{
            ...BTN('transparent'), color: 'var(--muted)',
            borderColor: 'var(--border)' }}>Отменить</button>
        </div>
      )}

      {/* Отдельно и внизу: трогает историю, а не справочник */}
      {data.can_edit && (
        <div style={{ marginTop: 22, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10,
                        lineHeight: 1.6, maxWidth: 720 }}>
            <b style={{ color: 'var(--text)' }}>Старые смены.</b> Режим отпечатывается
            на смене с того дня, как программа научилась это делать. У смен старше
            отпечатка нет, и отчёт сравнивает их с сегодняшним справочником, честно
            это оговаривая. Проставить им эталон задним числом можно — это решение
            о том, что нынешние цифры верны и для тех дней.
            <br />
            Уже отпечатанную смену не изменит ничто: её эталон — то задание, по
            которому она работала.
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={stampMissing} style={{
              ...BTN('transparent'), color: 'var(--text)', borderColor: 'var(--border)' }}>
              Проставить тем, у кого нет
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
